import { useEffect, useRef, type ReactNode } from 'react';
import { PageBleed } from './PageBleed';

export interface SectionedPageFrameProps {
  /** Section menu, shown at `lg` and up */
  sidebar: ReactNode;
  /** The same menu as a dropdown below `lg` */
  mobileNav: ReactNode;
  /** Title of the open section; omitted → no title bar */
  sectionTitle?: ReactNode;
  sectionTitleTestId?: string;
  contentTestId?: string;
  /**
   * Identity of the open section. When it changes, the content pane scrolls
   * back to the top: the window never scrolls on a `PageBody fill` screen, the
   * pane does, so a new section would otherwise open at the old one's offset.
   */
  sectionKey?: string;
  children: ReactNode;
}

/**
 * The two panes of a sectioned screen — settings, the admin panel, project
 * settings (#2078, program #2079): the section menu on the left, the open
 * section on the right, each scrolling on its own.
 *
 * Goes under `PageHeader` inside `<PageBody fill>`, and bleeds through the
 * body's `p-6` (`PageBleed`) like the records list: the header keeps the
 * page padding, the panes run to the edges.
 */
export function SectionedPageFrame({
  sidebar,
  mobileNav,
  sectionTitle,
  sectionTitleTestId,
  contentTestId,
  sectionKey,
  children,
}: SectionedPageFrameProps) {
  const paneRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (paneRef.current) paneRef.current.scrollTop = 0;
  }, [sectionKey]);

  return (
    <PageBleed data-testid="sectioned-page-frame">
      <div className="shrink-0 border-b border-border p-4 lg:hidden">{mobileNav}</div>
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside className="hidden w-60 shrink-0 overflow-y-auto border-r border-border p-4 lg:block">
          {sidebar}
        </aside>
        {/* pb-chat-safe keeps the end of a section (the team-list pagination,
            most visibly) scrollable clear of the Bitrix24 chat button parked in
            the bottom-right corner — see styles/chat-widget.css. */}
        <div
          ref={paneRef}
          data-testid="sectioned-page-pane"
          className="flex-1 overflow-y-auto pb-chat-safe"
        >
          {sectionTitle ? (
            <div className="sticky top-0 z-10 border-b border-border bg-bg/95 px-6 py-3 backdrop-blur supports-[backdrop-filter]:bg-bg/80">
              <h2
                className="text-base font-medium text-fg"
                data-testid={sectionTitleTestId}
              >
                {sectionTitle}
              </h2>
            </div>
          ) : null}
          <div className="p-6" data-testid={contentTestId}>
            {children}
          </div>
        </div>
      </div>
    </PageBleed>
  );
}
